import { EditOutlined } from '@ant-design/icons';
import { Box, Button, Dialog, Stack } from '@mui/material';
import VenueTypeDto from 'common/dto-types/venue-type-dto';
import IconButton from 'components/@extended/IconButton';
import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import BaseTable from 'components/base-table';
import { BaseTableFetchFuncProps, SortingType } from 'components/base-table/dto';
import { useMemo, useRef, useState } from 'react';
import { Filters, Row } from 'react-table';
import { mapFiltersToSpecification, mapSearchParamToSpecification } from 'utils/filtering/map-filter-to-specification';
import { PopupTransition } from '../../components/@extended/Transitions';
import { dispatch, useSelector } from '../../store';
import {
    closeForm,
    openCreateForm,
    setVenueType,
    setVenueTypeColumnsPersistence,
    setVenueTypeListPersistence,
} from '../../store/reducers/venue-type';
import { fetchVenueTypeList } from './api';
import VenueTypeTableColumnConfig, { SearchableVenueTypeTableColumnConfig } from './venue-type-column-config';
import VenueTypeDeleteButton from './venue-type-delete-button';
import VenueTypeForm from './venue-type-form';

type VenueTypeListQuery = {
    filters: Filters<any>;
    searchTerm?: string | null;
    sortBy?: string;
    sortingType?: SortingType;
};

const VenueTypeListPage = () => {
    const [venueTypeList, setVenueTypeList] = useState<Array<VenueTypeDto>>([]);
    const savedFilters = useSelector((state) => state.venueType.filters);
    const savedSorting = useSelector((state) => state.venueType.sorting);
    const defaultVisibleColumnConfig = useSelector((state) => state.venueType.visibleColumns);

    const isFormOpen = useSelector((state) => state.venueType.isFormOpen);

    const loadVenueTypes = async ({ filters, searchTerm, sortBy, sortingType }: VenueTypeListQuery) => {
        const data = await fetchVenueTypeList({
            sortBy,
            sortingType,
            andSpecifications: mapFiltersToSpecification(filters),
            orSpecifications:
                searchTerm && searchTerm.length > 0
                    ? mapSearchParamToSpecification(searchTerm, SearchableVenueTypeTableColumnConfig)
                    : undefined,
        });

        if (data) {
            setVenueTypeList(data);

            dispatch(
                setVenueTypeListPersistence({
                    filters: filters,
                    sorting: { searchTerm, sortBy, sortingType },
                }),
            );
        }
    };

    const fetchData: BaseTableFetchFuncProps = async ({ filters, searchTerm, sortBy, sortingType }) =>
        loadVenueTypes({ filters, searchTerm, sortBy, sortingType });

    const handleColumnVisibilityChange = async (newVisibleColumns: string[]) => {
        dispatch(setVenueTypeColumnsPersistence(newVisibleColumns));
    };

    const refreshVenueTypeList = async () => {
        await loadVenueTypes({
            filters: savedFilters,
            searchTerm: savedSorting.searchTerm,
            sortBy: savedSorting.sortBy,
            sortingType: savedSorting.sortingType,
        });
        dispatch(closeForm());
    };

    const refreshVenueTypeListRef = useRef(refreshVenueTypeList);
    refreshVenueTypeListRef.current = refreshVenueTypeList;

    const defaultColumnConfig = useMemo(
        () => [
            ...VenueTypeTableColumnConfig,
            {
                Header: 'Actions',
                Cell: (row: { row: Row }) => {
                    const venueType = row.row.original as VenueTypeDto;
                    return (
                        <Stack direction="row">
                            <IconButton
                                shape="rounded"
                                onClick={() => {
                                    dispatch(setVenueType(venueType));
                                    dispatch(openCreateForm());
                                }}
                            >
                                <EditOutlined />
                            </IconButton>
                            <VenueTypeDeleteButton
                                venueType={venueType}
                                refreshCallback={() => refreshVenueTypeListRef.current()}
                            />
                        </Stack>
                    );
                },
            },
        ],
        [],
    );

    return (
        <MainCard content={false}>
            <Stack direction="column">
                <Box sx={{ p: 2 }}>
                    <Button variant="contained" color="primary" onClick={() => dispatch(openCreateForm())}>
                        Add Venue Type
                    </Button>
                </Box>
                <ScrollX>
                    <BaseTable
                        data={venueTypeList}
                        fetchData={fetchData}
                        pageCount={0}
                        defaultFilters={savedFilters ?? []}
                        defaultSortingType={savedSorting.sortingType}
                        defaultSearchTerm={savedSorting.searchTerm}
                        defaultColumnIdToSortBy={savedSorting.sortBy}
                        defaultColumnConfig={defaultColumnConfig}
                        defaultVisibleColumnConfig={defaultVisibleColumnConfig}
                        onColumnsVisibilityChange={handleColumnVisibilityChange}
                        hidePagination
                    />
                </ScrollX>
            </Stack>
            {isFormOpen && (
                <Dialog
                    maxWidth="sm"
                    TransitionComponent={PopupTransition}
                    fullWidth
                    open={isFormOpen}
                    sx={{ '& .MuiDialog-paper': { p: 0 } }}
                >
                    <VenueTypeForm refreshCallback={() => refreshVenueTypeList()} />
                </Dialog>
            )}
        </MainCard>
    );
};

export default VenueTypeListPage;
